import CheckIsLoggedInGuard from 'app/core/guards/CheckIsLoggedInGuard';
import HasToBeAgencyGuard from 'app/core/guards/HasToBeAgencyGuard';
import HasToBeLoggedInGuard from 'app/core/guards/HasToBeLoggedInGuard';
import DefaultLayout from 'app/core/layout/DefaultLayout';
import InspectorAddProfile from 'app/main/agency/inspector-profile/InspectorAddProfile';
import InspectorGetDefaultProfile from 'app/main/agency/inspector-profile/InspectorGetDefaultProfile';
import AgencyMessagingRedirectPage from 'app/main/agency/messaging/AgencyMessagingRedirectPage';
import AgencyGetDefaultProfile from 'app/main/agency/profile/AgencyGetDefaultProfile';
import Error404Page from 'app/main/errors/Error404Page';
import { lazy } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';

import { getAgencyProfileRoutes } from './agency-routes/agencyProfileRoutes';
import { agencyTicketsRoutes } from './agency-routes/agencyTicketsRoutes';
import { inspectorProfileRoutes } from './agency-routes/inspectorProfileRoutes';

const AgencyDashboard = lazy(() => import('../../../main/agency/dashboard/AgencyDashboard'));
const PublicProfile = lazy(() => import('../../../main/agency/public-profile/PublicProfile'));
const AccountProfilePage = lazy(() => import('../../../main/agency/account/AccountProfilePage'));

const AgencyInvoiceDetailsPage = lazy(() => import('../../../main/agency/bookings/details/AgencyInvoiceDetailsPage'));
const AgencyPayoutDetailsPage = lazy(() => import('../../../main/agency/bookings/details/AgencyPayoutDetailsPage'));
const AgencyReceiptDetailsPage = lazy(() => import('../../../main/agency/bookings/details/AgencyReceiptDetailsPage'));
const AgencyBookingCreateInvoicePage = lazy(
  () => import('../../../main/agency/bookings/details/AgencyBookingCreateInvoicePage')
);
const AgencyBookingDetailsPage = lazy(() => import('../../../main/agency/bookings/details/AgencyBookingDetailsPage'));
const AgencyProposalViewPage = lazy(() => import('../../../main/agency/bookings/proposal/AgencyProposalViewPage'));
const AgencyProposalPage = lazy(() => import('../../../main/agency/bookings/proposal/AgencyProposalPage'));
const AgencyScheduleFixedPage = lazy(() => import('../../../main/agency/bookings/proposal/AgencyScheduleFixedPage'));
const AgencyReportDetailsPage = lazy(
  () => import('../../../main/agency/bookings/report-details/AgencyReportDetailsPage')
);
const EventReportReceiptDetailsPage = lazy(
  () => import('../../../main/agency/bookings/details/EventReportReceiptDetailsPage')
);
const AgencyBookingsPage = lazy(() => import('../../../main/agency/bookings/AgencyBookingsPage'));
const InspectionsListingSection = lazy(() => import('../../../main/agency/inspections/InspectionsListingSection'));
const AgencyCreateThreadPage = lazy(() => import('../../../main/agency/messaging/AgencyCreateThreadPage'));
const AgencyMessagingPage = lazy(() => import('../../../main/agency/messaging/AgencyMessagingPage'));
const AgencyNotificationLandingPage = lazy(
  () => import('../../../main/agency/messaging/AgencyNotificationLandingPage')
);
const AgencySettingsPage = lazy(() => import('../../../main/agency/settings/AgencySettingsPage'));
const AgencyPaymentsPage = lazy(() => import('../../../main/agency/payments/AgencyPaymentsPage'));
const AgencyReviewsPage = lazy(() => import('../../../main/agency/reviews/AgencyReviewsPage'));

const AgencyInspectorsPage = lazy(() => import('../../../main/agency/agency-inspectors/AgencyInspectorsPage'));

const PREFIX = '/agency';

const agencyRoutes: RouteObject[] = [
  // REDIRECTS
  {
    path: PREFIX,
    element: <Navigate to={PREFIX + '/dashboard'} />,
  },
  {
    path: '/ami',
    element: <AgencyMessagingRedirectPage />,
  },
  {
    path: '/ami/:id',
    element: <AgencyMessagingRedirectPage />,
  },
  {
    path: PREFIX + '/messaging',
    element: <Navigate to={PREFIX + '/messaging/inbox'} />,
  },
  {
    path: PREFIX + '/settings',
    element: <Navigate to={PREFIX + '/settings/general'} />,
  },
  {
    path: PREFIX + '/payments',
    element: <Navigate to={PREFIX + '/payments/payments-list'} />,
  },
  {
    path: PREFIX + '/bookings',
    element: <Navigate to={PREFIX + '/bookings/job-opportunities'} />,
  },
  {
    path: PREFIX,
    element: (
      <HasToBeLoggedInGuard>
        <HasToBeAgencyGuard>
          <DefaultLayout />
        </HasToBeAgencyGuard>
      </HasToBeLoggedInGuard>
    ),
    errorElement: <Error404Page />,
    children: [
      {
        path: 'dashboard',
        element: <AgencyDashboard />,
      },
      {
        path: 'account',
        element: <AccountProfilePage />,
      },
      {
        path: 'profile',
        element: <AgencyGetDefaultProfile />,
      },
      {
        path: ':id',
        children: getAgencyProfileRoutes([]),
      },
      {
        path: 'tickets',
        children: agencyTicketsRoutes,
      },
      {
        path: 'my-inspector',
        element: <InspectorGetDefaultProfile />,
      },
      // {
      //   path: 'inspector',
      //   element: <AgencyInspectorsPage />,
      // },
      {
        path: 'inspector/add',
        element: <InspectorAddProfile />,
      },
      {
        path: 'inspector/:id',
        children: inspectorProfileRoutes,
      },

      // BOOKINGS SECTION
      {
        path: 'bookings/:sectionId/invoices/:invoiceId',
        element: <AgencyInvoiceDetailsPage />,
      },
      {
        path: 'payouts/:payoutId',
        element: <AgencyPayoutDetailsPage />,
      },
      {
        path: 'bookings/:sectionId/invoices/:invoiceId/receipt',
        element: <AgencyReceiptDetailsPage />,
      },
      {
        path: 'bookings/confirmed-jobs/create-invoice/:leadId',
        element: <AgencyBookingCreateInvoicePage />,
      },
      {
        path: 'bookings/:sectionId/details/:leadId',
        element: <AgencyBookingDetailsPage />,
      },
      {
        path: 'bookings/:sectionId/proposal/:leadId/show',
        element: <AgencyProposalViewPage />,
      },
      {
        path: 'bookings/:sectionId/proposal/:leadId',
        element: <AgencyProposalPage />,
      },
      {
        path: 'bookings/:sectionId/schedule-fixed/:leadId',
        element: <AgencyScheduleFixedPage />,
      },
      {
        path: 'bookings/:sectionId/report/:reportId',
        element: <AgencyReportDetailsPage />,
      },
      {
        path: 'bookings/:sectionId/report/:reportId/receipt',
        element: <EventReportReceiptDetailsPage />,
      },
      {
        path: 'bookings/:sectionId?',
        element: <AgencyBookingsPage />,
      },
      // END BOOKINGS SECTION

      // INSPECTIONS SECTION
      {
        path: 'inspections/:sectionId',
        element: <InspectionsListingSection />,
      },

      // MESSAGES SECTION
      {
        path: 'messaging/create-thread/:recipientId',
        element: <AgencyCreateThreadPage />,
      },
      {
        path: 'messaging/create-thread/:recipientId/:jobId',
        element: <AgencyCreateThreadPage />,
      },

      {
        path: 'messaging/:sectionId',
        element: <AgencyMessagingPage />,
      },
      {
        path: 'messaging/:sectionId/:threadId',
        element: <AgencyMessagingPage />,
      },
      {
        path: 'notifications/:messageId',
        element: <AgencyNotificationLandingPage />,
      },

      // SETTINGS SECTION
      {
        path: 'settings/:sectionId',
        element: <AgencySettingsPage />,
      },

      // PAYMENTS
      {
        path: 'payments/:id',
        element: <AgencyPaymentsPage />,
      },

      // REVIEWS
      {
        path: 'reviews',
        element: <AgencyReviewsPage />,
      },
    ],
  },
  {
    path: PREFIX,
    element: (
      <CheckIsLoggedInGuard>
        <DefaultLayout />
      </CheckIsLoggedInGuard>
    ),
    errorElement: <Error404Page />,
    children: [
      {
        path: 'public/:id',
        element: <PublicProfile />,
      },
    ],
  },
];

export default agencyRoutes;
